import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  DescriptionOutlined,
  ExpandMore,
  OpenInNew,
} from '@mui/icons-material';
import { LD } from './tokens';

export interface DocumentSummaryData {
  id: string;
  documentName: string;
  documentType: string;
  summary: {
    sections?: Array<{ title: string; content: string }>;
    keyPoints?: string[];
    restrictions?: string[];
    fees?: Array<{ description: string; amount?: string; frequency?: string }>;
    deadlines?: string[];
    importantNotes?: string[];
  };
  s3Url: string;
  processedAt: string;
}

export const documentsSubtitle = (count: number): string => {
  if (count === 0) return 'None posted for this home yet';
  if (count === 1) return '1 document summarized';
  return `${count} documents summarized`;
};

interface DocumentsPanelProps {
  documentSummaries: DocumentSummaryData[];
}

/**
 * Summaries of the listing's documents (disclosures, HOA, restrictions). A
 * home with none says so; summaries still being prepared are not announced.
 */
const DocumentsPanel: React.FC<DocumentsPanelProps> = ({
  documentSummaries,
}) => {
  if (documentSummaries.length === 0) {
    return (
      <Typography sx={{ fontSize: '13.5px', color: LD.muted, lineHeight: 'normal' }}>
        The listing agent has not posted documents for this home yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {documentSummaries.map((doc: DocumentSummaryData) => (
        <Accordion
          key={doc.id}
          disableGutters
          elevation={0}
          sx={{
            bgcolor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              px: 2,
              minHeight: 48,
              '& .MuiAccordionSummary-content': {
                my: 1,
                alignItems: 'center',
                gap: 1,
              },
            }}
          >
            <DescriptionOutlined sx={{ fontSize: 18, color: '#6b7280' }} />
            <Typography
              fontWeight={600}
              fontSize="0.875rem"
              color="text.primary"
            >
              {doc.documentName}
            </Typography>
            <Chip
              label={doc.documentType}
              size="small"
              sx={{ fontSize: '0.6875rem', height: 20 }}
            />
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
            {doc.summary.keyPoints && doc.summary.keyPoints.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.8125rem' }}
                >
                  Key Points
                </Typography>
                {doc.summary.keyPoints.map((point: string, i: number) => (
                  <Typography
                    key={i}
                    variant="body2"
                    sx={{
                      fontSize: '0.8125rem',
                      pl: 1.5,
                      position: 'relative',
                      mb: 0.25,
                      color: '#374151',
                    }}
                  >
                    <span style={{ position: 'absolute', left: 0 }}>-</span>{' '}
                    {point}
                  </Typography>
                ))}
              </Box>
            )}

            {doc.summary.restrictions &&
              doc.summary.restrictions.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                      fontSize: '0.8125rem',
                      color: '#dc2626',
                    }}
                  >
                    Restrictions
                  </Typography>
                  {doc.summary.restrictions.map(
                    (restriction: string, i: number) => (
                      <Typography
                        key={i}
                        variant="body2"
                        sx={{
                          fontSize: '0.8125rem',
                          pl: 1.5,
                          position: 'relative',
                          mb: 0.25,
                          color: '#374151',
                        }}
                      >
                        <span style={{ position: 'absolute', left: 0 }}>-</span>{' '}
                        {restriction}
                      </Typography>
                    )
                  )}
                </Box>
              )}

            {doc.summary.fees && doc.summary.fees.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.8125rem' }}
                >
                  Fees & Assessments
                </Typography>
                {doc.summary.fees.map(
                  (
                    fee: {
                      description: string;
                      amount?: string;
                      frequency?: string;
                    },
                    i: number
                  ) => (
                    <Typography
                      key={i}
                      variant="body2"
                      sx={{
                        fontSize: '0.8125rem',
                        pl: 1.5,
                        position: 'relative',
                        mb: 0.25,
                        color: '#374151',
                      }}
                    >
                      <span style={{ position: 'absolute', left: 0 }}>-</span>
                      {fee.description}
                      {fee.amount ? `: ${fee.amount}` : ''}
                      {fee.frequency ? ` (${fee.frequency})` : ''}
                    </Typography>
                  )
                )}
              </Box>
            )}

            {doc.summary.deadlines && doc.summary.deadlines.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.8125rem' }}
                >
                  Deadlines & Time-Sensitive Items
                </Typography>
                {doc.summary.deadlines.map((deadline: string, i: number) => (
                  <Typography
                    key={i}
                    variant="body2"
                    sx={{
                      fontSize: '0.8125rem',
                      pl: 1.5,
                      position: 'relative',
                      mb: 0.25,
                      color: '#374151',
                    }}
                  >
                    <span style={{ position: 'absolute', left: 0 }}>-</span>{' '}
                    {deadline}
                  </Typography>
                ))}
              </Box>
            )}

            {doc.summary.importantNotes &&
              doc.summary.importantNotes.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.8125rem' }}
                  >
                    Important Notes
                  </Typography>
                  {doc.summary.importantNotes.map((note: string, i: number) => (
                    <Typography
                      key={i}
                      variant="body2"
                      sx={{
                        fontSize: '0.8125rem',
                        pl: 1.5,
                        position: 'relative',
                        mb: 0.25,
                        color: '#374151',
                      }}
                    >
                      <span style={{ position: 'absolute', left: 0 }}>-</span>{' '}
                      {note}
                    </Typography>
                  ))}
                </Box>
              )}

            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f3f4f6' }}>
              <Typography
                variant="caption"
                component="a"
                href={doc.s3Url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#1976d2',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                View original document <OpenInNew sx={{ fontSize: 12 }} />
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
};

export default DocumentsPanel;
